<script lang="ts">
import {
  defineComponent,
  PropType,
  getCurrentInstance,
  ref,
  computed,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from 'vue'
import throttle from 'lodash/throttle'
import AccordionTrigger from '@/components/accordion/AccordionTrigger.vue'
import AccordionBody from '@/components/accordion/AccordionBody.vue'
import backdrop from '@/models/backdrop'
import resizedWidth from '@/utilities/resizedWidth'
import scrollUtil from '@/utilities/scrollUtil'
import type { Ref } from 'vue'

const BACKDROP_CANCEL_ID = 'multiSelector'
const SELECTOR_ITEMS_MAX_HEIGHT = 300

export default defineComponent({
  name: 'MultiSelectorDefault',

  components: {
    AccordionTrigger,
    AccordionBody,
  },

  props: {
    /**
     * <select> 要素の valueを受け取る
     * 値が null の場合は value 指定なしとして内部データで処理を行う
     */
    modelValue: {
      type: Array as PropType<MultiSelect>,
      default: () => [],
    },

    /**
     * <select> 要素の <option> 部分に渡す配列
     *
     * @example [1, 2, 3, 4] : number 型の配列
     * @example ['1', '2', '3', '4'] : String 型の配列
     * @example [{ value: '1', label: 'ラベル1' }] : Object 型の配列 value と画面に表示する内容が異なる場合
     *
     */
    items: {
      type: Array as PropType<SelectorOption[]>,
      required: true,
    },

    /**
     * フォームの name 属性
     * 任意プロパティ
     */
    name: {
      type: String,
      default: '',
    },

    placeholder: {
      type: String,
      default: '複数選択できます',
    },

    /**
     * <option> の末尾に付与される単位
     * 任意プロパティ
     *
     * @example unit="個" => <option>1個</option>
     */
    unit: {
      type: String,
      default: '',
    },

    /**
     * セレクターの非活性フラグ
     * 任意プロパティ
     *
     * @usage 活性状態を管理する親の data を受け渡す
     */
    isDisabled: {
      type: Boolean,
      default: false,
    },

    /**
     * エラー発生状態かどうか
     * ※ エラー状態は外から受け取る
     */
    isError: {
      type: Boolean,
      default: false,
    },

    /**
     * 選択のリセットボタンを表示するかどうか
     */
    hasReset: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue', 'change'],

  setup(props, { emit }) {
    // model の backdrop の処理実体を定義
    backdrop(() => {
      isShowItems.value = false
    }, BACKDROP_CANCEL_ID)

    /**
     * コンポーネント内部でもつ選択状態の値
     * 親コンポーネントから value プロパティ を受け取らない場合は、このプロパティの値が selector に反映される
     */
    const localValue: Ref<MultiSelect> = ref([])
    const localItems: Ref<SelectItem[]> = ref([])
    const isShowItems = ref(false)
    const root = ref()
    const selectorEl = ref()
    const itemsPositionStyle = ref('')
    const maxWidth = ref('')
    const overflowHiddenBlockEl: Ref<HTMLElement | undefined> = ref()

    /**
     * 選択された要素のラベル配列
     */
    const selectedLabels = computed<string[]>(() => {
      return props.items.filter(v => {
        return (localValue.value as ReadonlyArray<string | number>).includes(itemValue(v))
      }).map(v => itemLabel(v))
    })

    watch(() => props.modelValue, (newValue) => {
      if (localValue.value !== newValue) localValue.value = newValue
    })

    /**
     * [リアクティブデータの描画漏れ回避策]
     *
     * option の値が更新されたら強制描画する
     */
    watch(() => props.items, (newValue) => {
      if (newValue.length) {
        const instance = getCurrentInstance()
        nextTick(() => {
          instance?.proxy?.$forceUpdate()
          initItems()
        })
      }
    }, { deep: true })

    /**
     * セレクターが開かれた際にドロップダウン部分の位置の再計算
     */
    watch(isShowItems, (newValue) => {
      if (newValue) {
        findOverflowHiddenWrapper()
        initItemsPosition()
      }
    })

    onMounted(() => {
      localValue.value = props.modelValue
      initItems()
    })

    nextTick(() => {
      initMaxWidth()
      findOverflowHiddenWrapper()
      initItemsPosition()
    })

    /**
     * 「選択肢」の初期化
     */
    const initItems = () => {
      if (!props.items.length) {
        console.warn('No selector options are passed to the component.')
        return
      }

      const itemValues = props.items.map(item => itemValue(item)) as Array<string |  number | undefined>

      // props で渡された選択肢配列の重複チェック
      // Set は重複を自動的に排除するため、重複があった場合、length にズレが生じる
      const itemValueSets = new Set(itemValues)
      if (itemValueSets.size !== itemValues.length) {
        console.warn('There are duplicate items in the selector options.')
      }

      // items 配列の初期化
      localItems.value = []

      props.items.forEach(v => {
        localItems.value.push({
          label: itemLabel(v),
          value: itemValue(v),
          disabled: itemDisabled(v),
        })
      })
    }

    /**
     * item の値
     *
     * @param item {String|Object}
     */
    const itemValue = (item: SelectorOption): string | number => {
      return (typeof item === 'object' ? item.value : item) as string | number
    }

    /**
     * item 表示用のラベル
     *
     * @param item {String|Object}
     */
    const itemLabel = (item: SelectorOption): string => {
      return typeof item === 'object' ? item.label.toString() : item.toString()
    }

    const itemDisabled = (item: SelectorOption): boolean => {
      return typeof item === 'object' && !!item.disabled
    }

    /**
     * v-for の key
     * @description value だけ・label だけではそれぞれ重複しうるため、組で一意にする
     * （label の重複例: 開始日時が同じで表示文言なしの開催枠「2026年10月26日(月) 14:00-」）
     */
    const itemKey = (item: SelectItem): string => JSON.stringify([item.value, item.label])

    const initMaxWidth = () => {
      maxWidth.value = ''
      nextTick(() => {
        if (root.value) {
          maxWidth.value = getComputedStyle(root.value.parentNode).width
        }
      })
    }

    /**
     * 先祖 DOM を再帰的に取得し続け、overflow: hidden が設定されているラッパーを探索する
     *
     * @description body まで到達したら終了するように設定されているが、念の為探索上限を 70 とする
     */
    const findOverflowHiddenWrapper = () => {
      let node = root.value ? root.value : undefined
      if (!node) return

      let remainingLoop = 70 // 無限ループ対策
      overflowHiddenBlockEl.value = undefined
      while(!!remainingLoop && node.tagName.toUpperCase() !== 'BODY') {
        node = node.parentNode
        const style = window.getComputedStyle(node)
        if (style && style.getPropertyValue('overflow').toUpperCase() === 'HIDDEN') {
          overflowHiddenBlockEl.value = node
          break
        }
        remainingLoop--
      }
      remainingLoop = 70
    }

    /**
     * ドロップダウンリストの表示位置計算
     */
    const initItemsPosition = () => {
      if (!localItems.value.length || !selectorEl.value) return

      // サジェストのボタン 1 つの高さ（ブラウザ環境で誤差は出るので、大まかな概算値）
      const SELECTOR_ITEM_HEIGHT = 58

      // 入力フォームの画面表示位置
      const SELECTOR_POSITION = selectorEl.value.getBoundingClientRect().top

      // サジェスト全体の高さ
      const itemsHeight = SELECTOR_ITEMS_MAX_HEIGHT < localItems.value.length * SELECTOR_ITEM_HEIGHT
        ? SELECTOR_ITEMS_MAX_HEIGHT
        : localItems.value.length * SELECTOR_ITEM_HEIGHT

      // ドロップダウンリストを表示する位置を算出する上で基準となる高さ（overflow: hidden が設定されている block か、もしくは document 全体）
      let referenceHeight = 0

      /**
       * overflow: hidden が先祖に設定されている場合、画面の高さではなく、その先祖ラッパー側の高さで計算する
       * （overflow: hidden の影響で、ドロップダウンリストの表示が隠れてしまうため）
       */
      let OVERFLOW_HIDDEN_BLOCK_POSITION
      if (overflowHiddenBlockEl.value) {
        OVERFLOW_HIDDEN_BLOCK_POSITION = overflowHiddenBlockEl.value.getBoundingClientRect().top
        if (document.documentElement.clientHeight < (OVERFLOW_HIDDEN_BLOCK_POSITION + overflowHiddenBlockEl.value.offsetHeight)) {
          referenceHeight = document.documentElement.clientHeight
        } else {
          referenceHeight = OVERFLOW_HIDDEN_BLOCK_POSITION + overflowHiddenBlockEl.value.offsetHeight
        }
      } else {
        referenceHeight = document.documentElement.clientHeight
      }

      /**
       * 下に表示できるスペースがある場合は下にドロップダウン表示する
       * 下にスペースがない場合、
       *    先祖に overflow: hidden が設定されており、且つドロップダウンを上に表示した際に、その overflow: hidden の範囲内に収まっていない場合は、超過分だけ position を下にずらす
       *    上表示で収まっている場合はそのままセレクターの上部に表示する
       */
      itemsPositionStyle.value = referenceHeight > (SELECTOR_POSITION + selectorEl.value.offsetHeight + itemsHeight)
        ? `top: ${selectorEl.value.offsetHeight}px`
        : overflowHiddenBlockEl.value && OVERFLOW_HIDDEN_BLOCK_POSITION! > (SELECTOR_POSITION - itemsHeight)
          ? `bottom: ${selectorEl.value.offsetHeight - (OVERFLOW_HIDDEN_BLOCK_POSITION! - (SELECTOR_POSITION - itemsHeight))}px`
          : `bottom: ${selectorEl.value.offsetHeight}px`
    }

    /**
     * 選択された際の発火イベント
     * @param target 選択された項目の DOM
     */
    const onChange = (value: string | number, target: HTMLInputElement) => {
      emit('update:modelValue', localValue.value)
      emit('change', value, target)
    }

    const reset = () => {
      localValue.value = []
      emit('update:modelValue', localValue.value)
    }

    /**
     * window のリサイズイベントを監視して、幅に変更があったらサジェストの表示位置を再取得
     */
    const resizeListener = throttle(async () => {
      initMaxWidth()
      initItemsPosition()
    }, 50)

    const cleanupScroll = scrollUtil.throttle({
      callback: () => {
        initItemsPosition()
      },
      wait: 10,
    })

    window.addEventListener('resize', resizeListener)

    onBeforeUnmount(() => {
      // イベントリスナー解除
      cleanupScroll()
      window.removeEventListener('resize', resizeListener)
    })

    return {
      // constants
      BACKDROP_CANCEL_ID,

      // data
      localValue,
      localItems,
      isShowItems,
      root,
      selectorEl,
      itemsPositionStyle,
      maxWidth,

      // computed
      selectedLabels,

      // methods
      itemValue,
      itemLabel,
      itemDisabled,
      itemKey,
      initItemsPosition,
      onChange,
      reset,
    }
  }
})
</script>

<template>
  <div
    v-if="items"
    ref="root"
    class="o-selector__multi"
    :data-backdrop-cancel="BACKDROP_CANCEL_ID"
    :data-focus="isShowItems"
    :data-disabled="isDisabled"
    :data-error="isError"
    :style="{
      'max-width': maxWidth,
    }">
    <!--
      セレクターがレンダリングされていない状態での block の基本幅を max-width とするため、
      max-width の算出が完了するまでレンダリングしない
    -->
    <dl
      v-show="maxWidth"
      class="o-selector__multi--unit">
      <dt
        ref="selectorEl"
        class="o-selector__multi--term">
        <div
          class="o-action__multiSelector"
          :data-focus="isShowItems"
          :data-disabled="isDisabled"
          :data-error="isError"
          :data-selected="!!(localValue.length)">
          <AccordionTrigger
            v-model:open-state="isShowItems"
            class="o-action__multiSelector--item"
            tag-name="button"
            :disabled="isDisabled">
            {{ selectedLabels.join('、') || placeholder }}
          </AccordionTrigger>
        <!-- /.o-action__multiSelector --></div>
      </dt>

      <transition name="is-fade">
        <dd
          v-show="isShowItems"
          class="o-selector__multi--description"
          :style="itemsPositionStyle">
          <AccordionBody
            class="o-list__multiSelector"
            :open-state="isShowItems"
            unit-of-visible-height="px">
            <ul class="o-list__multiSelector--items">
              <li
                v-if="hasReset"
                class="o-list__multiSelector--item">
                <div class="o-action__reset">
                  <button
                    class="o-action__reset--item"
                    @click.prevent="reset">
                    選択を解除
                  </button>
                <!-- o-action__reset --></div>
              </li>
              <li
                v-for="item in localItems"
                :key="itemKey(item)"
                class="o-list__multiSelector--item">
                <div
                  class="o-selector__checkboxItem"
                  :data-disabled="item.disabled">
                  <label class="o-selector__checkboxItem--label">
                    <input
                      v-model="localValue"
                      class="o-selector__checkboxItem--item"
                      type="checkbox"
                      :name="name ? `${name}[]` : undefined"
                      :value="item.value"
                      :disabled="isDisabled || item.disabled"
                      @change="onChange((item.value as string | number), ($event.target as HTMLInputElement))">
                    {{ item.label }}{{ unit }}
                  </label>
                </div>
              </li>
            </ul>
          </AccordionBody>
        </dd>
      </transition>
    </dl>
  <!-- /.o-selector__multi --></div>
</template>

<style lang="sass" scoped>
.o-selector__multi
  $root: &
  width: 100%
  position: relative
  margin:
    top: 14px
    left: 0
    right: auto

  &:first-child
    margin-top: 0

  & &--description
    width: 100%
    max-height: 300px
    position: absolute
    overflow-y: auto
    background-color: rgba($bg-xx-light-cyan, 1)
    border: 1px solid $border-xx-light-cyan
    border-radius: $border-radius
    box-shadow: $box-shadow
    z-index: 2

.o-list__multiSelector
  & &--item
    border-top: 1px solid $border-x-light-gray

    &:first-child
      border-top: none

.o-action__multiSelector
  $root: &

  & &--item
    width: 100%
    min-height: 1em
    display: block
    position: relative
    overflow: hidden
    text-align: left
    text-overflow: ellipsis
    padding: 0.6em 30px 0.6em 1em
    background-color: rgba($bg-xx-light-cyan, 1)
    border: 1px solid $border-xx-light-cyan
    border-radius: $border-radius
    white-space: nowrap
    +transition

    &::after
      +iconTriangle(down)
      position: absolute
      top: 50%
      right: 10px
      transform: translateY(-50%)
      +transition

    +mouseover
      border-color: $border-light-cyan

  &[data-focus="true"]
    #{$root}--item
      background-color: $bg-white
      border-color: $border-light-cyan
      box-shadow: $box-shadow

      &::after
        transform: translateY(-50%) rotate(-180deg)

  &[data-disabled="true"]
    #{$root}--item
      border-color: $border-xx-light-cyan
      opacity: 0.4
      cursor: default

      &::after
        opacity: 0.3

  &[data-error="true"]
    #{$root}--item
      background-color: rgba($bg-error, 1)
      border-color: $border-error

  &[data-selected="false"]
    #{$root}--item
      color: rgba($font-black, 0.4)

.o-action__reset
  & &--item
    width: 100%
    min-height: 1em
    display: block
    text-align: left
    padding: 0.5em 1em
    +transition

    +mouseover
      background-color: rgba($bg-x-light-cyan, 1)

.o-selector__checkboxItem
  $root: &

  & &--label
    width: 100%
    min-height: 1em
    display: block
    padding: 0.5em 1em
    +transition

    +mouseover
      background-color: rgba($bg-x-light-cyan, 1)

  &[data-disabled="true"]
    #{$root}--label
      opacity: 0.5
      cursor: default

      +mouseover
        background-color: transparent

  & &--item
    +checkboxStyle
    +transition
    cursor: pointer

    &:checked
      background-color: rgba($bg-xx-light-cyan, 1)
      border: 1px solid $border-xx-light-cyan

      &::after
        opacity: 1

    +mouseover
      &:not(:checked)
        border-color: $border-light-cyan

    &:disabled
      cursor: default

      &:not(:checked)
        border-color: $border-xx-light-cyan

    &[data-is-error="true"]
      background-color: rgba($bg-error, 1)
      border-color: $border-error

      &:not(:checked)
        border-color: $border-error
</style>
